import { ELIGIBILITY_SUMMARY_STATES } from '@wodi/domain';
import { z } from 'zod';

import { codedValueSchema, reasonSchema } from './clinical-common.ts';

// SHA: eligibility, the SHA visit and settings (docs/plans/phase1-sha.md §8). Everything
// SHA returns is shown as SHA said it: code, display and system as returned. Money is
// cents as decimal strings. Identifier values never appear here: a check records which
// identifier system it used, not the value.

/** Whether this deployment talks to SHA, and how (plan P2: the operator's choice). */
export const SHA_CONNECTIONS = ['not_connected', 'test'] as const;
export type ShaConnection = (typeof SHA_CONNECTIONS)[number];

export const ELIGIBILITY_IDENTIFIER_SYSTEMS = ['cr_id', 'national_id'] as const;

const codedSchema = codedValueSchema;

const centsString = z.string().regex(/^-?\d+$/);
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const eligibilityCheckRequestSchema = z.object({
  /** Which identifier to send. Absent: the CR ID, else the national ID (plan S1). */
  identifierSystem: z.enum(ELIGIBILITY_IDENTIFIER_SYSTEMS).optional(),
  visitId: z.uuid().optional(),
});
export type EligibilityCheckRequest = z.infer<typeof eligibilityCheckRequestSchema>;

/** A check made on the SHA portal, recorded by hand while not connected (plan P1). */
export const manualEligibilityRequestSchema = z.object({
  schemeId: z.uuid(),
  /** The portal's reference for the check. */
  reference: z.string().trim().min(1).max(60),
  reason: reasonSchema,
  visitId: z.uuid().optional(),
});
export type ManualEligibilityRequest = z.infer<typeof manualEligibilityRequestSchema>;

export const eligibilitySchemeViewSchema = z.object({
  schemeId: z.uuid(),
  scheme: codedSchema,
  status: codedSchema,
  outcome: z.enum(['covered', 'not_covered', 'unknown']),
  coverageStart: isoDay.nullable(),
  coverageEnd: isoDay.nullable(),
  waitingPeriodEnds: isoDay.nullable(),
  memberNumber: z.string().nullable(),
  reasons: z.array(codedSchema),
  limits: z.array(
    z.object({
      label: z.string(),
      remainingCents: centsString.nullable(),
      limitCents: centsString.nullable(),
    }),
  ),
  /** The scheme in one line, e.g. "SHIF: covered until 30 Jun 2027". */
  line: z.string(),
  /** The scheme was new to this organisation and waits for finance to review it. */
  needsReview: z.boolean(),
});
export type EligibilitySchemeView = z.infer<typeof eligibilitySchemeViewSchema>;

export { ELIGIBILITY_SUMMARY_STATES };

export const eligibilityCheckViewSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  visitId: z.uuid().nullable(),
  checkedAt: z.iso.datetime(),
  mode: z.enum(['online', 'recorded_by_hand']),
  outcome: z.enum(['answered', 'unavailable', 'not_configured', 'refused']),
  errorCode: z.string().nullable(),
  identifierSystem: z.string().nullable(),
  manualReference: z.string().nullable(),
  summary: z.object({ state: z.enum(ELIGIBILITY_SUMMARY_STATES), words: z.string() }),
  /** What happened and what to do, when SHA gave no answer. */
  problem: z.string().nullable(),
  schemes: z.array(eligibilitySchemeViewSchema),
});
export type EligibilityCheckView = z.infer<typeof eligibilityCheckViewSchema>;

export const eligibilityCheckListSchema = z.object({ items: z.array(eligibilityCheckViewSchema) });

export const SHA_VISIT_STATES = [
  'waiting_to_start',
  'started',
  'failed',
  'not_connected',
  'closed',
] as const;
export type ShaVisitStateView = (typeof SHA_VISIT_STATES)[number];

export const visitShaSchema = z.object({
  visitId: z.uuid(),
  connection: z.enum(SHA_CONNECTIONS),
  coverage: z
    .object({
      id: z.uuid(),
      schemeId: z.uuid(),
      schemeName: z.string(),
      memberNumber: z.string().nullable(),
      periodEnd: isoDay.nullable(),
    })
    .nullable(),
  check: eligibilityCheckViewSchema.nullable(),
  shaVisit: z
    .object({
      state: z.enum(SHA_VISIT_STATES),
      /** In words, e.g. "SHA visit started" or "SHA visit couldn't start: …". */
      words: z.string(),
      externalReference: z.string().nullable(),
      lastErrorCode: z.string().nullable(),
      attempts: z.number().int(),
    })
    .nullable(),
});
export type VisitShaView = z.infer<typeof visitShaSchema>;

export const shaConnectionSchema = z.object({ connection: z.enum(SHA_CONNECTIONS) });

/** Active schemes under the SHA payer, for recording a portal check by hand. */
export const shaSchemeListSchema = z.object({
  items: z.array(z.object({ id: z.uuid(), name: z.string(), needsReview: z.boolean() })),
});

export const shaSettingsSchema = z.object({
  facilityId: z.uuid(),
  connection: z.enum(SHA_CONNECTIONS),
  providerIdentifier: z.string().nullable(),
  claimTrigger: z.enum(['on_visit_close', 'manual']),
  eligibilityMaxAgeMinutes: z.number().int(),
  version: z.number().int(),
});
export type ShaSettingsView = z.infer<typeof shaSettingsSchema>;

export const updateShaSettingsRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  providerIdentifier: z.string().trim().max(60).nullable().optional(),
  claimTrigger: z.enum(['on_visit_close', 'manual']).optional(),
  eligibilityMaxAgeMinutes: z.number().int().min(5).max(10_080).optional(),
});
export type UpdateShaSettingsRequest = z.infer<typeof updateShaSettingsRequestSchema>;
